
import Account from '@/components/Account';
import Notification from '@/components/Notification';
import useHeaderMenuOptions from '@/queries/useHeaderMenuOptions';
import useStoreApp from '@/store';
import { useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import MenuItem, { MenuItemTypeMap } from '@mui/material/MenuItem';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import DirectionsCarFilledRoundedIcon from '@mui/icons-material/DirectionsCarFilledRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { useRouter } from 'next/router';
import { useRouter as useRouterNext13 } from 'next13-progressbar';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';
import {
  LoginIcon,
  UsaFlagIcon,
  VnFlagIcon,
  KoreaFlagIcon,
} from '../SvgIcons';

import {
  CompactBookingTabItem,
  CompactBookingTabs,
  ControlLogin,
  ControlNotLogin,
  HeaderMenuDropdown,
  HeaderMenuDropdownItem,
  HeaderMenuArrow,
  HeaderActionButton,
  HeaderActionButtons,
  HeaderMenuLabelRow,
  HeaderMenuLoadingBox,
  HeaderMenuTriggerTab,
  HeaderMenuWrapper,
  StyledHeader,
  StyledLogo,
  StyledSelectHeader,
  StyledLoginButtonBox,
  FlagBox,
} from './style';
import { queryToHomeUrl } from '@/utils/utils';

const cookie = require('js-cookie');

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'EN', icon: <UsaFlagIcon /> },
  { value: 'vi', label: 'VN', icon: <VnFlagIcon /> },
  { value: 'ko', label: 'KR', icon: <KoreaFlagIcon /> },
];

export enum TabType {
  ETAXI = 'e_taxi',
  TOUR = 'tour',
}

const STATIC_HEADER_TABS = [
  { labelKey: 'travelGuide', path: '/hoiantravelguide' },
  { labelKey: 'about', path: '/about' },
] as const;

const normalizeComparablePath = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;

  const basePath = path.split('?')[0].trim();
  const normalizedPath = `/${basePath}`.replace(/\/{2,}/g, '/');

  if (normalizedPath === '/') return normalizedPath;
  return normalizedPath.replace(/\/+$/, '');
};

interface Props {
  showTabs?: boolean;
}

function Header({ showTabs }: Props) {
  const { t } = useTranslation(['common']);
  const router = useRouter();
  const routerNext13 = useRouterNext13();
  const { user, languages, setLanguageId, inPayment } = useStoreApp(
    state => state.globalSlice,
  );
  const [selectedTab, setSelectedTab] = useState<TabType | null>(null);
  const [selectedNavigationPath, setSelectedNavigationPath] = useState('');
  const [isNavigationDropdownOpen, setIsNavigationDropdownOpen] = useState(false);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const closeDropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    data: headerMenuOptions = [],
    isLoading: isHeaderMenuLoading,
  } = useHeaderMenuOptions(router.locale);

  const [isScrollTop, setIsScrollTop] = useState(false);

  const handleLocaleChange = useCallback(
    async (event: any) => {
      const value = event.target.value;
      cookie.set('LOCALE', value);
      const { pathname, query, asPath } = router;
      router.push({ pathname, query }, asPath, {
        locale: value,
      });
    },
    [router],
  );

  useEffect(() => {
    const currentPath = router.asPath.split('?')[0];
    const isHomePath = currentPath === '/';

    if (!isHomePath) {
      setSelectedTab(null);
      return;
    }

    if (
      typeof router.query.tab === 'string' &&
      Object.values(TabType).includes(router.query.tab as TabType)
    ) {
      setSelectedTab(router.query.tab as TabType);
      return;
    }

  }, [router.asPath, router.query.tab]);

  useEffect(() => {
    if (languages) {
      const lang = languages.find((e: any) => {
        if (router.locale === 'vi') return e.code.toLowerCase() === 'vn';
        else return e.code.toLowerCase() === 'en';
      });

      if (lang) setLanguageId(lang?.id);
    }
  }, [router.locale, languages]);

  useEffect(() => {
    const detail_tour_container = document.getElementById('hoiango-detail-tour-booking-container');
    const scrollNext = () => {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (detail_tour_container) {
          detail_tour_container.style.minHeight = Math.min(640, window.innerHeight - 132) + 'px';
        }

        setIsScrollTop(true);
      }
    }

    if (inPayment && !isScrollTop) {
      const timeout = setTimeout(scrollNext, 300);

      return () => {
        clearTimeout(timeout);
        if (detail_tour_container) {
          detail_tour_container.style.minHeight = 'calc(100dvh - 132px)';
        }
        setIsScrollTop(false);
      };
    }

  }, [inPayment]);

  useEffect(() => {
    if (!headerMenuOptions.length) {
      const currentPath = normalizeComparablePath(router.asPath);
      if (currentPath === '/hoiantours' || currentPath.startsWith('/hoiantours/')) {
        setSelectedNavigationPath('/hoiantours');
        return;
      }
      setSelectedNavigationPath('');
      return;
    }
    const currentPath = normalizeComparablePath(router.asPath);
    if (currentPath === '/hoiantours' || currentPath.startsWith('/hoiantours/')) {
      setSelectedNavigationPath('/hoiantours');
      return;
    }
    const selectedOption = headerMenuOptions.find(
      option => normalizeComparablePath(option.path) === currentPath,
    );
    setSelectedNavigationPath(selectedOption?.path ?? '');
  }, [headerMenuOptions, router.asPath]);

  const handleChangeTab = (tab: TabType) => {
    const currentPath = '/';
    const currentQuery = router.query;

    if (!!user) {
      if (currentQuery.collaboratorId) {
        delete currentQuery.collaboratorId;
      }

      if (currentQuery.promotionName) {
        delete currentQuery.promotionName;
      }

      if (currentQuery.address) {
        delete currentQuery.address;
      }

      if (currentQuery.lat) {
        delete currentQuery.lat;
      }

      if (currentQuery.lng) {
        delete currentQuery.lng;
      }
    }

    if (currentQuery.tourType) {
      delete currentQuery.tourType;
      if (currentQuery.slug) {
        delete currentQuery.slug;
      }
    }

    const newQuery = { ...currentQuery, tab: tab };

    const url = {
      pathname: currentPath,
      query: newQuery,
    };

    router.push(url);
  };

  const handleNavigationChange = useCallback(
    async (path: string) => {
      setSelectedNavigationPath(path);
      if (!path) return;
      setIsNavigationDropdownOpen(false);
      await router.push(path);
    },
    [router],
  );

  const clearCloseDropdownTimeout = () => {
    if (!closeDropdownTimeoutRef.current) return;
    clearTimeout(closeDropdownTimeoutRef.current);
    closeDropdownTimeoutRef.current = null;
  };

  const openNavigationDropdown = () => {
    clearCloseDropdownTimeout();
    setIsNavigationDropdownOpen(true);
  };

  const closeNavigationDropdown = () => {
    clearCloseDropdownTimeout();
    closeDropdownTimeoutRef.current = setTimeout(() => {
      setIsNavigationDropdownOpen(false);
    }, 160);
  };

  const handleNavigationTriggerClick = useCallback(async () => {
    setSelectedNavigationPath('/hoiantours');
    setIsNavigationDropdownOpen(false);
    await router.push('/hoiantours');
  }, [router]);

  const handleNavigateStaticTab = useCallback(
    async (path: string) => {
      await router.push(path);
    },
    [router],
  );

  useEffect(() => {
    return () => {
      clearCloseDropdownTimeout();
    };
  }, []);

  const isMobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const handleOpenMobileMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMobileMenu = useCallback(() => {
    setMobileMenuAnchorEl(null);
  }, []);

  const handleMobileNavigationChange = useCallback(
    async (path: string) => {
      handleCloseMobileMenu();
      await handleNavigationChange(path);
    },
    [handleCloseMobileMenu, handleNavigationChange],
  );

  const handleMobileNavigateStaticTab = useCallback(
    async (path: string) => {
      handleCloseMobileMenu();
      await handleNavigateStaticTab(path);
    },
    [handleCloseMobileMenu, handleNavigateStaticTab],
  );

  const handleMobileLoginClick = useCallback(async () => {
    handleCloseMobileMenu();
    if (router.asPath === '/login/') {
      await router.push(`/`);
      return;
    }
    await router.push(`/login`);
  }, [handleCloseMobileMenu, router]);

  const handleMobileLocaleSelect = useCallback(
    async (locale: string) => {
      handleCloseMobileMenu();
      cookie.set('LOCALE', locale);
      const { pathname, query, asPath } = router;
      await router.push({ pathname, query }, asPath, {
        locale,
      });
    },
    [handleCloseMobileMenu, router],
  );

  return (
    <Container maxWidth="xl" disableGutters>
      <StyledHeader>
        <StyledLogo href={queryToHomeUrl(user !== null ? `dashboard` : `${cookie.get('LOCALE') ?? ''}`, router)}>
          <Image src="/assets/images/logo.png" alt="logo" width={80} height={24} />
          {/* <Box>
            HOIAN<span> GO</span>
          </Box> */}
        </StyledLogo>

        {showTabs && !isMobile && user == null && (
          <CompactBookingTabs>
            {(isHeaderMenuLoading || headerMenuOptions.length > 0) && !inPayment && (
              <HeaderMenuWrapper
                onMouseEnter={openNavigationDropdown}
                onMouseLeave={closeNavigationDropdown}
              >
                <HeaderMenuTriggerTab
                  className={selectedNavigationPath ? 'active' : ''}
                  onClick={handleNavigationTriggerClick}
                >
                  <HeaderMenuLabelRow>
                    {t('tourOptions')}
                    <HeaderMenuArrow className={isNavigationDropdownOpen ? 'open' : ''}>
                      <KeyboardArrowDownRoundedIcon sx={{ fontSize: '18px' }} />
                    </HeaderMenuArrow>
                  </HeaderMenuLabelRow>
                </HeaderMenuTriggerTab>
                <HeaderMenuDropdown className={isNavigationDropdownOpen ? 'open' : ''}>
                  {isHeaderMenuLoading ? (
                    <HeaderMenuLoadingBox>
                      <CircularProgress size={22} />
                    </HeaderMenuLoadingBox>
                  ) : (
                    headerMenuOptions.map(option => (
                      <HeaderMenuDropdownItem
                        key={option.value}
                        className={
                          normalizeComparablePath(option.path) ===
                            normalizeComparablePath(selectedNavigationPath)
                            ? 'active'
                            : ''
                        }
                        onClick={() => handleNavigationChange(option.path)}
                      >
                        {option.label}
                      </HeaderMenuDropdownItem>
                    ))
                  )}
                </HeaderMenuDropdown>
              </HeaderMenuWrapper>
            )}

            {
              STATIC_HEADER_TABS.map(tab => (
                <CompactBookingTabItem
                  key={tab.labelKey}
                  className={
                    normalizeComparablePath(router.asPath) === normalizeComparablePath(tab.path)
                      ? 'active'
                      : ''
                  }
                  onClick={() => handleNavigateStaticTab(tab.path)}
                >
                  {t(tab.labelKey)}
                </CompactBookingTabItem>
              ))}

          </CompactBookingTabs>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: {
              xs: '8px',
              sm: '0'
            },
          }}
        >
          {showTabs && !inPayment && !isMobile && (
            <HeaderActionButtons>
              <HeaderActionButton
                className={selectedTab === TabType.TOUR ? 'tour active' : 'tour'}
                onClick={() => handleChangeTab(TabType.TOUR)}
              >
                <EventRoundedIcon sx={{ fontSize: '18px' }} />
                {t('tourBooking')}
              </HeaderActionButton>
              <HeaderActionButton
                className={selectedTab === TabType.ETAXI ? 'etaxi active' : 'etaxi'}
                onClick={() => handleChangeTab(TabType.ETAXI)}
              >
                <DirectionsCarFilledRoundedIcon sx={{ fontSize: '18px' }} />
                {t('home.bookETaxi')}
              </HeaderActionButton>
            </HeaderActionButtons>
          )}

          {showTabs && !inPayment && isMobile && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '4px' }}>
                <HeaderActionButton
                  className={selectedTab === TabType.TOUR ? 'tour active' : 'tour'}
                  onClick={() => handleChangeTab(TabType.TOUR)}
                  sx={{
                    height: '36px',
                    px: '14px',
                    fontSize: '15px',
                    lineHeight: '18px',
                  }}
                >
                  {t('tourBooking')}
                </HeaderActionButton>
                <HeaderActionButton
                  className={selectedTab === TabType.ETAXI ? 'etaxi active' : 'etaxi'}
                  onClick={() => handleChangeTab(TabType.ETAXI)}
                  sx={{
                    height: '36px',
                    px: '14px',
                    fontSize: '15px',
                    lineHeight: '18px',
                  }}
                >
                  {t('home.bookETaxi')}
                </HeaderActionButton>
              </Box>

              <IconButton
                onClick={handleOpenMobileMenu}
                sx={{
                  color: theme.palette.white.main,
                  background: 'transparent',
                  borderRadius: '6px',
                  width: '36px',
                  height: '36px',
                  marginRight: '2px',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.12)',
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>

              <Menu
                anchorEl={mobileMenuAnchorEl}
                open={isMobileMenuOpen}
                onClose={handleCloseMobileMenu}
                PaperProps={{
                  sx: {
                    minWidth: '260px',
                    mt: 1,
                    borderRadius: '10px',
                  },
                }}
              >
                {user && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: "column",
                      alignItems: 'start',
                      gap: '12px',
                      paddingX: '8px',
                      paddingTop: '12px',
                    }}
                  >
                    <Box display={'flex'} alignItems={'center'} gap={'12px'}>
                      <Account title={user.fullName} />
                    </Box>
                    <Box display={'flex'} alignItems={'center'} gap={'12px'}>
                      <Notification />
                      <Typography variant="body2">{t('notify')}</Typography>
                    </Box>
                  </Box>
                )}
                {user && (
                  <Box paddingY={1}>
                    <Divider />
                  </Box>
                )}
                {!user && (
                  <MenuItem onClick={() => handleMobileNavigationChange('/hoiantours')}>
                    {t('tourOptions')}
                  </MenuItem>
                )}
                {!user &&
                  headerMenuOptions.map(option => (
                    <MenuItem
                      key={`mobile-${option.value}`}
                      onClick={() => handleMobileNavigationChange(option.path)}
                      sx={{ pl: 3 }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                {!user &&
                  STATIC_HEADER_TABS.map(tab => (
                    <MenuItem
                      key={`mobile-${tab.labelKey}`}
                      onClick={() => handleMobileNavigateStaticTab(tab.path)}
                    >
                      {t(tab.labelKey)}
                    </MenuItem>
                  ))}
                {!user && <Divider />}
                {LANGUAGE_OPTIONS.map(option => (
                  <MenuItem
                    key={`mobile-locale-${option.value}`}
                    onClick={() => handleMobileLocaleSelect(option.value)}
                    selected={router.locale === option.value}
                  >
                    <FlagBox sx={{ mr: 1 }}>
                      {option.icon}
                    </FlagBox>
                    {option.label}
                  </MenuItem>
                ))}

                {!user && !(typeof window !== 'undefined' && window.location.hostname === process.env.NEXT_PUBLIC_DOMAIN_EN) && (
                  <MenuItem onClick={handleMobileLoginClick}>
                    <LoginIcon />
                    <Box component="span" sx={{ ml: 1 }} suppressHydrationWarning>
                      {router.asPath === '/login/' ? t('booking') : t('login')}
                    </Box>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}

          <StyledSelectHeader
            onChange={handleLocaleChange}
            value={router.locale}
            sx={{
              display: inPayment || (showTabs && isMobile) ? 'none' : undefined,
            }}
          >
            {LANGUAGE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <FlagBox>
                  {option.icon}
                </FlagBox>
                {option.label}
              </MenuItem>
            ))}
          </StyledSelectHeader>

          {showTabs && isMobile ? null : user ? (
            <ControlLogin>
              <Notification />
              <Account />
            </ControlLogin>
          ) : typeof window !== 'undefined' && window.location.hostname === process.env.NEXT_PUBLIC_DOMAIN_EN ? null : (
            <ControlNotLogin>
              <Button
                size="small"
                variant="text"
                onClick={() =>
                  router.asPath === '/login/'
                    ? router.push(`/`)
                    : router.push(`/login`)
                }
                sx={{
                  marginX: `${isMobile ? '4' : '12'}px`,
                  minWidth: `${isMobile ? '40' : '64'}px`,
                }}
              >
                <StyledLoginButtonBox display="flex" gap={1} alignItems="center" sx={{ color: theme.palette.white.main }} suppressHydrationWarning>
                  <LoginIcon />
                  {!isMobile &&
                    (router.asPath === '/login/' ? t('booking') : t('login'))}
                </StyledLoginButtonBox>
              </Button>
            </ControlNotLogin>
          )}
        </Box>
      </StyledHeader>
    </Container >
  );
}

interface FlagIconProps {
  prefix?: React.ReactNode;
  name?: string;
}

const FlagIcon = (props: FlagIconProps & MenuItemTypeMap) => {
  const { prefix, name, ...otherProps } = props;

  return (
    <MenuItem {...otherProps}>
      {
        prefix ?
          <Typography component="p" sx={{ width: '20px', height: '20px', borderRadius: '50%', overflow: 'hidden' }}>
            {prefix}
          </Typography> :
          null
      }
      {name ? <Typography component="p">{name}</Typography> : null}
    </MenuItem>
  )
}

export default memo(Header);
